import type { Clip, Subtitle, Timeline, Track } from "../types/timeline";
import type { TextClip } from "../text/types";
import type { ShapeClip } from "../graphics/types";
import { titleEngine } from "../text/title-engine";
import { graphicsEngine } from "../graphics/graphics-engine";

/**
 * Timeline queries: given a project and a time, which items are on screen.
 * Pure, and they read nothing off the engine.
 */

export function getActiveTextClips(timeline: Timeline, time: number): TextClip[] {
  const allTextClips = titleEngine.getAllTextClips();
  const textTracks = timeline.tracks.filter(
    (t) => t.type === "text" && !t.hidden,
  );
  const textTrackIds = new Set(textTracks.map((t) => t.id));

  return allTextClips.filter((clip) => {
    if (!textTrackIds.has(clip.trackId)) return false;
    const clipEnd = clip.startTime + clip.duration;
    return time >= clip.startTime && time < clipEnd;
  });
}

export function getActiveShapeClips(timeline: Timeline, time: number): ShapeClip[] {
  const allShapeClips = graphicsEngine.getAllShapeClips();
  const graphicsTracks = timeline.tracks.filter(
    (t) => t.type === "graphics" && !t.hidden,
  );
  const graphicsTrackIds = new Set(graphicsTracks.map((t) => t.id));

  return allShapeClips.filter((clip) => {
    if (!graphicsTrackIds.has(clip.trackId)) return false;
    const clipEnd = clip.startTime + clip.duration;
    return time >= clip.startTime && time < clipEnd;
  });
}

export function getActiveSVGClips(
  timeline: Timeline,
  time: number,
): import("../graphics/types").SVGClip[] {
  const allSVGClips = graphicsEngine.getAllSVGClips();
  const graphicsTracks = timeline.tracks.filter(
    (t) => t.type === "graphics" && !t.hidden,
  );
  const graphicsTrackIds = new Set(graphicsTracks.map((t) => t.id));

  return allSVGClips.filter((clip) => {
    if (!graphicsTrackIds.has(clip.trackId)) return false;
    const clipEnd = clip.startTime + clip.duration;
    return time >= clip.startTime && time < clipEnd;
  });
}

export function getActiveStickerClips(
  timeline: Timeline,
  time: number,
): import("../graphics/types").StickerClip[] {
  const allStickerClips = graphicsEngine.getAllStickerClips();
  const graphicsTracks = timeline.tracks.filter(
    (t) => t.type === "graphics" && !t.hidden,
  );
  const graphicsTrackIds = new Set(graphicsTracks.map((t) => t.id));

  return allStickerClips.filter((clip) => {
    if (!graphicsTrackIds.has(clip.trackId)) return false;
    const clipEnd = clip.startTime + clip.duration;
    return time >= clip.startTime && time < clipEnd;
  });
}

export function getActiveSubtitles(timeline: Timeline, time: number): Subtitle[] {
  const subtitles = timeline.subtitles || [];
  return subtitles.filter((sub) => {
    return time >= sub.startTime && time < sub.endTime;
  });
}

export function getClipsAtTime(track: Track, time: number): Clip[] {
  return track.clips.filter((clip) => {
    const clipEnd = clip.startTime + clip.duration;
    return time >= clip.startTime && time < clipEnd;
  });
}
